import tw, { theme, css } from 'twin.macro';
import {
  BgColor,
  BorderColor,
  Color,
  TextColor,
} from '@teetsh/app/src/types/colors';
import {
  tagBackgroundColorNames,
  tagBackgroundColors,
  tagColors,
  tagColorToTextColor,
  TagColorWithIntensity,
  tagTextColor,
  tagTextColorForWhiteBg,
} from '@teetsh/app/src/styles/userConstants';
import { SerializedStyles } from '@emotion/serialize';
import { TwStyle } from 'twin.macro';
import { adjustTailwindColorForPrint } from '@teetsh/app/src/utils/adjustTailwindColorForPrint';
import { backgroundColors } from '@teetsh/app/src/styles/themeConstants';
import _sample from 'lodash-es/sample';

export const colors = {
  black: theme('colors.black'),

  // Text colors
  'text-brand-intense': theme('textColor.brand-intense'),
  'text-brand-moderate': theme('textColor.brand-moderate'),
  'text-brand-quiet': theme('textColor.brand-quiet'),
  'text-neutral-intense': theme('textColor.neutral-intense'),
  'text-neutral-moderate': theme('textColor.neutral-moderate'),
  'text-neutral-quiet': theme('textColor.neutral-quiet'),
  'text-neutral-supershy': theme('textColor.neutral-supershy'),
  'text-danger-intense': theme('textColor.danger-intense'),
  'text-danger-moderate': theme('textColor.danger-moderate'),
  'text-danger-quiet': theme('textColor.danger-quiet'),
  'text-warning-intense': theme('textColor.warning-intense'),
  'text-warning-moderate': theme('textColor.warning-moderate'),
  'text-warning-quiet': theme('textColor.warning-quiet'),
  'text-positive-intense': theme('textColor.positive-intense'),
  'text-positive-moderate': theme('textColor.positive-moderate'),
  'text-positive-quiet': theme('textColor.positive-quiet'),
  'text-reverse-intense': theme('textColor.reverse-intense'),
  'text-reverse-moderate': theme('textColor.reverse-moderate'),
  'text-reverse-quiet': theme('textColor.reverse-quiet'),
  'text-reverse-supershy': theme('textColor.reverse-supershy'),

  // Background colors
  'bg-brand-intense': theme('backgroundColor.brand-intense'),
  'bg-brand-moderate': theme('backgroundColor.brand-moderate'),
  'bg-brand-quiet': theme('backgroundColor.brand-quiet'),
  'bg-neutral-intense': theme('backgroundColor.neutral-intense'),
  'bg-neutral-moderate': theme('backgroundColor.neutral-moderate'),
  'bg-neutral-quiet': theme('backgroundColor.neutral-quiet'),
  'bg-neutral-supershy': theme('backgroundColor.neutral-supershy'),
  'bg-neutral-white': theme('backgroundColor.neutral-white'),
  'bg-danger-intense': theme('backgroundColor.danger-intense'),
  'bg-danger-moderate': theme('backgroundColor.danger-moderate'),
  'bg-danger-quiet': theme('backgroundColor.danger-quiet'),
  'bg-warning-intense': theme('backgroundColor.warning-intense'),
  'bg-warning-moderate': theme('backgroundColor.warning-moderate'),
  'bg-warning-quiet': theme('backgroundColor.warning-quiet'),
  'bg-positive-intense': theme('backgroundColor.positive-intense'),
  'bg-positive-moderate': theme('backgroundColor.positive-moderate'),
  'bg-positive-quiet': theme('backgroundColor.positive-quiet'),
  'bg-reverse-intense': theme('backgroundColor.reverse-intense'),
  'bg-reverse-moderate': theme('backgroundColor.reverse-moderate'),
  'bg-reverse-quiet': theme('backgroundColor.reverse-quiet'),

  // Border colors
  'border-brand-intense': theme('borderColor.brand-intense'),
  'border-brand-moderate': theme('borderColor.brand-moderate'),
  'border-brand-quiet': theme('borderColor.brand-quiet'),
  'border-neutral-intense': theme('borderColor.neutral-intense'),
  'border-neutral-moderate': theme('borderColor.neutral-moderate'),
  'border-neutral-quiet': theme('borderColor.neutral-quiet'),
  'border-danger-intense': theme('borderColor.danger-intense'),
  'border-danger-moderate': theme('borderColor.danger-moderate'),
  'border-danger-quiet': theme('borderColor.danger-quiet'),
  'border-warning-intense': theme('borderColor.warning-intense'),
  'border-warning-moderate': theme('borderColor.warning-moderate'),
  'border-warning-quiet': theme('borderColor.warning-quiet'),
  'border-positive-intense': theme('borderColor.positive-intense'),
  'border-positive-moderate': theme('borderColor.positive-moderate'),
  'border-positive-quiet': theme('borderColor.positive-quiet'),
  'border-reverse-moderate': theme('borderColor.reverse-moderate'),
  'border-reverse-quiet': theme('borderColor.reverse-quiet'),

  'gray-900': theme('colors.gray.900'),
  'gray-800': theme('colors.gray.800'),
  'gray-700': theme('colors.gray.700'),
  'gray-600': theme('colors.gray.600'),
  'gray-500': theme('colors.gray.500'),
  'gray-400': theme('colors.gray.400'),
  'gray-300': theme('colors.gray.300'),
  'gray-200': theme('colors.gray.200'),
  'gray-100': theme('colors.gray.100'),
  'gray-50': theme('colors.gray.50'),
  'purple-100': theme('colors.purple.100'),
  'purple-200': theme('colors.purple.200'),
  'purple-300': theme('colors.purple.300'),
  'purple-400': theme('colors.purple.400'),
  'purple-50': theme('colors.purple.50'),
  'purple-500': theme('colors.purple.500'),
  'purple-600': theme('colors.purple.600'),
  'purple-700': theme('colors.purple.700'),
  'purple-800': theme('colors.purple.800'),
  'purple-900': theme('colors.purple.900'),
  'violet-100': theme('colors.violet.100'),
  'violet-200': theme('colors.violet.200'),
  'violet-300': theme('colors.violet.300'),
  'violet-400': theme('colors.violet.400'),
  'violet-50': theme('colors.violet.50'),
  'violet-500': theme('colors.violet.500'),
  'violet-600': theme('colors.violet.600'),
  'violet-700': theme('colors.violet.700'),
  'violet-800': theme('colors.violet.800'),
  'violet-900': theme('colors.violet.900'),
  'cyan-100': theme('colors.cyan.100'),
  'cyan-200': theme('colors.cyan.200'),
  'cyan-300': theme('colors.cyan.300'),
  'cyan-400': theme('colors.cyan.400'),
  'cyan-50': theme('colors.cyan.50'),
  'cyan-500': theme('colors.cyan.500'),
  'cyan-600': theme('colors.cyan.600'),
  'cyan-700': theme('colors.cyan.700'),
  'cyan-800': theme('colors.cyan.800'),
  'cyan-900': theme('colors.cyan.900'),
  'amber-100': theme('colors.amber.100'),
  'amber-200': theme('colors.amber.200'),
  'amber-300': theme('colors.amber.300'),
  'amber-400': theme('colors.amber.400'),
  'amber-50': theme('colors.amber.50'),
  'amber-500': theme('colors.amber.500'),
  'amber-600': theme('colors.amber.600'),
  'amber-700': theme('colors.amber.700'),
  'amber-800': theme('colors.amber.800'),
  'amber-900': theme('colors.amber.900'),
  'cobalt-100': theme('colors.cobalt.100'),
  'cobalt-200': theme('colors.cobalt.200'),
  'cobalt-300': theme('colors.cobalt.300'),
  'cobalt-400': theme('colors.cobalt.400'),
  'cobalt-50': theme('colors.cobalt.50'),
  'cobalt-500': theme('colors.cobalt.500'),
  'cobalt-600': theme('colors.cobalt.600'),
  'cobalt-700': theme('colors.cobalt.700'),
  'cobalt-800': theme('colors.cobalt.800'),
  'cobalt-900': theme('colors.cobalt.900'),
  'emerald-100': theme('colors.emerald.100'),
  'emerald-200': theme('colors.emerald.200'),
  'emerald-300': theme('colors.emerald.300'),
  'emerald-400': theme('colors.emerald.400'),
  'emerald-50': theme('colors.emerald.50'),
  'emerald-500': theme('colors.emerald.500'),
  'emerald-600': theme('colors.emerald.600'),
  'emerald-700': theme('colors.emerald.700'),
  'emerald-800': theme('colors.emerald.800'),
  'emerald-900': theme('colors.emerald.900'),
  'green-100': theme('colors.green.100'),
  'green-200': theme('colors.green.200'),
  'green-300': theme('colors.green.300'),
  'green-400': theme('colors.green.400'),
  'green-50': theme('colors.green.50'),
  'green-500': theme('colors.green.500'),
  'green-600': theme('colors.green.600'),
  'green-700': theme('colors.green.700'),
  'green-800': theme('colors.green.800'),
  'green-900': theme('colors.green.900'),
  'indigo-100': theme('colors.indigo.100'),
  'indigo-200': theme('colors.indigo.200'),
  'indigo-300': theme('colors.indigo.300'),
  'indigo-400': theme('colors.indigo.400'),
  'indigo-50': theme('colors.indigo.50'),
  'indigo-500': theme('colors.indigo.500'),
  'indigo-600': theme('colors.indigo.600'),
  'indigo-700': theme('colors.indigo.700'),
  'indigo-800': theme('colors.indigo.800'),
  'indigo-900': theme('colors.indigo.900'),
  'blue-100': theme('colors.blue.100'),
  'blue-200': theme('colors.blue.200'),
  'blue-300': theme('colors.blue.300'),
  'blue-400': theme('colors.blue.400'),
  'blue-50': theme('colors.blue.50'),
  'blue-500': theme('colors.blue.500'),
  'blue-600': theme('colors.blue.600'),
  'blue-700': theme('colors.blue.700'),
  'blue-800': theme('colors.blue.800'),
  'blue-900': theme('colors.blue.900'),
  'lime-100': theme('colors.lime.100'),
  'lime-200': theme('colors.lime.200'),
  'lime-300': theme('colors.lime.300'),
  'lime-400': theme('colors.lime.400'),
  'lime-50': theme('colors.lime.50'),
  'lime-500': theme('colors.lime.500'),
  'lime-600': theme('colors.lime.600'),
  'lime-700': theme('colors.lime.700'),
  'lime-800': theme('colors.lime.800'),
  'lime-900': theme('colors.lime.900'),
  'orange-100': theme('colors.orange.100'),
  'orange-200': theme('colors.orange.200'),
  'orange-300': theme('colors.orange.300'),
  'orange-400': theme('colors.orange.400'),
  'orange-50': theme('colors.orange.50'),
  'orange-500': theme('colors.orange.500'),
  'orange-600': theme('colors.orange.600'),
  'orange-700': theme('colors.orange.700'),
  'orange-800': theme('colors.orange.800'),
  'orange-900': theme('colors.orange.900'),
  'pink-100': theme('colors.pink.100'),
  'pink-200': theme('colors.pink.200'),
  'pink-300': theme('colors.pink.300'),
  'pink-400': theme('colors.pink.400'),
  'pink-50': theme('colors.pink.50'),
  'pink-500': theme('colors.pink.500'),
  'pink-600': theme('colors.pink.600'),
  'pink-700': theme('colors.pink.700'),
  'pink-800': theme('colors.pink.800'),
  'pink-900': theme('colors.pink.900'),
  'red-100': theme('colors.red.100'),
  'red-200': theme('colors.red.200'),
  'red-300': theme('colors.red.300'),
  'red-400': theme('colors.red.400'),
  'red-50': theme('colors.red.50'),
  'red-500': theme('colors.red.500'),
  'red-600': theme('colors.red.600'),
  'red-700': theme('colors.red.700'),
  'red-800': theme('colors.red.800'),
  'red-900': theme('colors.red.900'),
  'rose-100': theme('colors.rose.100'),
  'rose-200': theme('colors.rose.200'),
  'rose-300': theme('colors.rose.300'),
  'rose-400': theme('colors.rose.400'),
  'rose-50': theme('colors.rose.50'),
  'rose-500': theme('colors.rose.500'),
  'rose-600': theme('colors.rose.600'),
  'rose-700': theme('colors.rose.700'),
  'rose-800': theme('colors.rose.800'),
  'rose-900': theme('colors.rose.900'),
  'teal-100': theme('colors.teal.100'),
  'teal-200': theme('colors.teal.200'),
  'teal-300': theme('colors.teal.300'),
  'teal-400': theme('colors.teal.400'),
  'teal-50': theme('colors.teal.50'),
  'teal-500': theme('colors.teal.500'),
  'teal-600': theme('colors.teal.600'),
  'teal-700': theme('colors.teal.700'),
  'teal-800': theme('colors.teal.800'),
  'teal-900': theme('colors.teal.900'),
  'yellow-100': theme('colors.yellow.100'),
  'yellow-200': theme('colors.yellow.200'),
  'yellow-300': theme('colors.yellow.300'),
  'yellow-400': theme('colors.yellow.400'),
  'yellow-50': theme('colors.yellow.50'),
  'yellow-500': theme('colors.yellow.500'),
  'yellow-600': theme('colors.yellow.600'),
  'yellow-700': theme('colors.yellow.700'),
  'yellow-800': theme('colors.yellow.800'),
  'yellow-900': theme('colors.yellow.900'),
  white: theme('colors.white'),
};

export function getColorHexOrFallback(
  color: Color | BgColor | TagColorWithIntensity,
) {
  const colorFromNewPalette = tagBackgroundColors[color];
  if (colorFromNewPalette) {
    return colorFromNewPalette;
  }

  // bad color names could come from old data and make the app crash
  const foundColor = colors[color];
  if (foundColor) {
    return foundColor;
  }
  const intentColor = colors[`bg-${color}`];
  if (intentColor) {
    return intentColor;
  }
  return theme('colors.gray.500');
}

export function bgColors(
  color: Color | BgColor | TagColorWithIntensity,
): SerializedStyles {
  const colorFromNewPalette = tagBackgroundColors[color];
  if (colorFromNewPalette) {
    return css({ backgroundColor: colorFromNewPalette });
  }
  const foundColor = colors[color];
  if (foundColor) {
    return css({ backgroundColor: foundColor });
  }
  const intentColor = colors[`bg-${color}`];
  if (intentColor) {
    return css({ backgroundColor: intentColor });
  }
  // fallback for safety
  return css({ backgroundColor: theme('colors.gray.500') });
}

export function borderColors(
  color: Color | BgColor | BorderColor | TagColorWithIntensity,
): SerializedStyles {
  const colorFromNewPalette = tagBackgroundColors[color];
  if (colorFromNewPalette) {
    return css({ borderColor: colorFromNewPalette });
  }
  const intentColor = colors[`bg-${color}`];
  if (intentColor) {
    return css({ borderColor: intentColor });
  }
  return css({ borderColor: colors[color] });
}

export function textColors(
  color: Color | TextColor | TagColorWithIntensity,
): SerializedStyles {
  return css({ color: colors[color] });
}

export function decorationColors(color: Color): SerializedStyles {
  return css({ textDecorationColor: colors[color] });
}

export function getTextColorFromBackgroundColor(
  bg?: Color | TagColorWithIntensity,
  important?: boolean,
): SerializedStyles | TwStyle {
  if (!bg) {
    return important ? tw`text-black!` : tw`text-black`;
  }

  const colorFromNewPalette = tagBackgroundColors[bg];
  if (colorFromNewPalette) {
    const textColor = tagColorToTextColor[bg];
    return css({ color: tagTextColor[textColor] });
  }

  // fallback on dynamic text color for legacy palette
  const colorNumber: number = parseInt(bg.split('-')[1], 10);
  if (colorNumber > 400) {
    return important ? tw`text-white!` : tw`text-white`;
  }
  if (bg === 'black') {
    return tw`text-white`;
  }
  return important ? tw`text-black!` : tw`text-black`;
}

export function getTitleColorFromTagColor(
  tagColor?: Color | TagColorWithIntensity,
): SerializedStyles {
  if (!tagColor) {
    return css({ color: theme('colors.gray.500') });
  }
  const colorFromNewPalette: string | undefined = tagBackgroundColors[tagColor];
  if (colorFromNewPalette) {
    const colorName = tagColorToTextColor[tagColor];
    return css({ color: tagTextColorForWhiteBg[colorName] });
  }

  const colorFromLegacyPalette =
    colors[adjustTailwindColorForPrint(tagColor as Color)];
  if (colorFromLegacyPalette) {
    return css({ color: colorFromLegacyPalette });
  }

  return css({ color: theme('colors.gray.500') });
}

export function randomBackgroundColor(): TagColorWithIntensity {
  return _sample(tagBackgroundColorNames) || 'gris-intense';
}

export function randomBackgroundColorModerate(): TagColorWithIntensity {
  return (
    _sample(tagColors.map(t => `${t}-moderate` as TagColorWithIntensity)) ||
    'gris-moderate'
  );
}

export function getUnderlineColorFromTagColor(
  tagColor?: Color | TagColorWithIntensity,
): SerializedStyles {
  if (!tagColor) {
    return css({ textDecorationColor: theme('colors.gray.500') });
  }
  const colorFromNewPalette: string | undefined = tagBackgroundColors[tagColor];
  if (colorFromNewPalette) {
    const colorName = tagColorToTextColor[tagColor];
    return css({ textDecorationColor: tagTextColorForWhiteBg[colorName] });
  }

  const colorFromLegacyPalette =
    colors[adjustTailwindColorForPrint(tagColor as Color)];
  if (colorFromLegacyPalette) {
    return css({ textDecorationColor: colorFromLegacyPalette });
  }

  return css({ textDecorationColor: theme('colors.gray.500') });
}

// tailwindcss-forms uses text color to style native inputs
// but in our design system surface colors and text colors have different values
// so we override the color style attribute using our surface color
export const surfaceBrandColorAsTextColorStyle: SerializedStyles = css({
  color: backgroundColors['brand-intense'],
});
